/**
 * Shared TSPL command generation for USB label printing.
 * Used by SFGMainLabelPrinting and ComboAssemblyStation.
 */

export const PRINTER_CONFIGS = {
  TSC_TE210: {
    name: 'TSC TE210 (Desktop)',
    dpi: 203,
    speed: 4,
    density: 8,
    vendorId: 0x1203
  },
  TSC_MH240: {
    name: 'TSC MH240 (Industrial)',
    dpi: 203,
    speed: 6,
    density: 10,
    vendorId: 0x1203
  }
};

/**
 * Generate TSPL commands from label data and template.
 * @param {Object} labelData - Label field values (quality, color, content, size, qty, mrp, qr2_code, etc.)
 * @param {Object} template - Template with fields, label_width_dots, label_height_dots, template_type
 * @param {string} usnCode - USN code for QR code 1
 * @param {string} mrpFormatted - Formatted MRP string
 * @param {string|null} displayUsn - Optional display text for USN (e.g. with pair number appended)
 * @param {Object|null} printerConfig - Printer configuration (speed, density, etc.)
 * @param {string|null} templateType - Override template type
 * @param {number|null} pairNumber - Pair number for box labels
 * @param {number} arialLineSpacingMultiplier - Line spacing multiplier for consumer notice
 * @returns {string} TSPL command string
 */
export const generateTsplFromLabelData = (
  labelData,
  template,
  usnCode,
  mrpFormatted,
  displayUsn = null,
  printerConfig = null,
  templateType = null,
  pairNumber = null,
  arialLineSpacingMultiplier = 2.5
) => {
  const config = printerConfig || PRINTER_CONFIGS.TSC_TE210;
  const widthMm = template ? Math.round(template.label_width_dots / 8) : 100;
  const heightMm = template ? Math.round(template.label_height_dots / 8) : 80;
  const usnDisplay = displayUsn || usnCode;
  const fields = template?.fields || {};
  const tplType = templateType || template?.template_type || '';

  const getField = (key, defaultX = 0, defaultY = 0, defaultFontSize = 2, defaultSize = 4) => {
    const field = fields[key] || {};
    return {
      x: field.x ?? defaultX,
      y: field.y ?? defaultY,
      font_size: field.font_size ?? defaultFontSize,
      bold: field.bold ?? false,
      size: field.size ?? defaultSize,
      text: field.text || ''
    };
  };

  const generateTextCmd = (x, y, fontSize, text) => {
    const arialSize = fontSize || 10.5;
    return `TEXT ${x},${y},"ARIALB.TTF",0,${arialSize},${arialSize},"${text}"\r\n`;
  };

  let tspl = '';

  tspl += `SIZE ${widthMm} mm, ${heightMm} mm\r\n`;
  tspl += `GAP 3 mm, 0 mm\r\n`;
  tspl += `SPEED ${config.speed}\r\n`;
  tspl += `DENSITY ${config.density}\r\n`;
  tspl += `DIRECTION 1,0\r\n`;
  tspl += `REFERENCE 0,0\r\n`;
  tspl += `OFFSET 0 mm\r\n`;
  tspl += `SET PEEL OFF\r\n`;
  tspl += `SET CUTTER OFF\r\n`;
  tspl += `SET PARTIAL_CUTTER OFF\r\n`;
  tspl += `CLS\r\n`;

  // QUALITY
  const qualityLabel = getField('quality_label', 16, 16, 10.5);
  const qualityValue = getField('quality_value', 160, 16, 14.0);
  if (labelData.quality) {
    tspl += generateTextCmd(qualityLabel.x, qualityLabel.y, qualityLabel.font_size, qualityLabel.text || 'QUALITY');
    tspl += generateTextCmd(qualityValue.x, qualityValue.y, qualityValue.font_size, labelData.quality);
  }

  // COLOR
  if (labelData.color) {
    const colorLabel = getField('color_label', 16, 56, 10.5);
    const colorValue = getField('color_value', 160, 56, 10.5);
    tspl += generateTextCmd(colorLabel.x, colorLabel.y, colorLabel.font_size, colorLabel.text || 'COLOR');
    tspl += generateTextCmd(colorValue.x, colorValue.y, colorValue.font_size, labelData.color);
  }

  // CONTENT
  const contentLabel = getField('content_label', 16, 96, 10.5);
  const contentValue = getField('content_value', 160, 96, 14.0);
  if (labelData.content1) {
    let contentParts = [labelData.content1];
    if (labelData.content2) contentParts.push(labelData.content2);
    if (labelData.content3) contentParts.push(labelData.content3);
    const content = contentParts.join('    ');
    tspl += generateTextCmd(contentLabel.x, contentLabel.y, contentLabel.font_size, contentLabel.text || 'CONTENT');
    tspl += generateTextCmd(contentValue.x, contentValue.y, contentValue.font_size, content);
  }

  // SIZE
  const sizeLabel = getField('size_label', 16, 136, 10.5);
  const sizeValue = getField('size_value', 160, 136, 10.5);
  let sizeParts = [];
  if (labelData.size1) sizeParts.push(labelData.size1);
  if (labelData.size2) sizeParts.push(labelData.size2);
  if (labelData.size3) sizeParts.push(labelData.size3);
  const size = sizeParts.length > 0 ? sizeParts.join('    ') : (labelData.size_from_rates || '');
  if (size) {
    tspl += generateTextCmd(sizeLabel.x, sizeLabel.y, sizeLabel.font_size, sizeLabel.text || 'SIZE');
    tspl += generateTextCmd(sizeValue.x, sizeValue.y, sizeValue.font_size, size);
  }

  // DESC5 Bold for combo
  if (labelData.desc5_bold && tplType?.includes('combo')) {
    const desc5Field = getField('desc5_bold', 400, 56, 14.0);
    tspl += generateTextCmd(desc5Field.x, desc5Field.y, desc5Field.font_size, labelData.desc5_bold);
  }

  // QTY (value configurable via template — falls back to label data / default)
  const qtyField = getField('qty', 16, 176, 10.5);
  const qtyVal = template?.fields?.qty?.value;
  const qtyText = (qtyVal !== undefined && qtyVal !== null && qtyVal !== '')
    ? `QTY : ${qtyVal} N`
    : (labelData.qty || 'QTY : 1 N');
  tspl += generateTextCmd(qtyField.x, qtyField.y, qtyField.font_size, qtyText);

  // MRP
  const mrpLabel = getField('mrp_label', 200, 176, 10.5);
  const mrpValue = getField('mrp_value', 280, 166, 18.0);
  tspl += generateTextCmd(mrpLabel.x, mrpLabel.y, mrpLabel.font_size, mrpLabel.text || 'MRP :');
  tspl += generateTextCmd(mrpValue.x, mrpValue.y, mrpValue.font_size, mrpFormatted);

  // WSP (wholesale price) — renders only when the template positions the WSP
  // field AND the rate carries a WSP value. Keeps existing labels untouched.
  const wspValue = getField('wsp_value', 0, 0, 18.0);
  const wspText = labelData.wsp_formatted
    || (labelData.wsp ? `Rs.${Number(labelData.wsp).toFixed(2)}` : '');
  if ((wspValue.x || wspValue.y) && wspText) {
    const wspLabel = getField('wsp_label', 0, 0, 10.5);
    if (wspLabel.x || wspLabel.y) {
      tspl += generateTextCmd(wspLabel.x, wspLabel.y, wspLabel.font_size, wspLabel.text || 'WSP :');
    }
    tspl += generateTextCmd(wspValue.x, wspValue.y, wspValue.font_size, wspText);
  }

  // Tax Notice
  const taxNotice = getField('tax_notice', 280, 210, 7.5);
  tspl += generateTextCmd(taxNotice.x, taxNotice.y, taxNotice.font_size, '(INCL. OF ALL TAXES)');

  // MADE IN INDIA
  const madeInIndia = getField('made_in_india', 16, 250, 10.5);
  tspl += generateTextCmd(madeInIndia.x, madeInIndia.y, madeInIndia.font_size, 'MADE IN INDIA');

  // QR Code 1 (USN)
  const qr1 = getField('qr1', 16, 290, 10.5, 5);
  const qr1Text = getField('qr1_text', 16, 410, 10.5);
  if (usnCode) {
    tspl += `QRCODE ${qr1.x},${qr1.y},L,${qr1.size},A,0,"${usnCode}"\r\n`;
    // Show USN code with pair number in parentheses if provided (for combo
    // pair matching — product sticker and box sticker carry the same USN
    // suffixed with the pair number, per user spec 2026-05-11).
    const usnLabel = pairNumber ? `${usnDisplay} (${pairNumber})` : usnDisplay;
    tspl += generateTextCmd(qr1Text.x, qr1Text.y, qr1Text.font_size, usnLabel);
  }

  // QR Code 2 (Barcode)
  const qr2 = getField('qr2', 200, 290, 10.5, 5);
  const qr2Text = getField('qr2_text', 200, 410, 10.5);
  if (labelData.qr2_code) {
    tspl += `QRCODE ${qr2.x},${qr2.y},L,${qr2.size},A,0,"${labelData.qr2_code}"\r\n`;
    tspl += generateTextCmd(qr2Text.x, qr2Text.y, qr2Text.font_size, labelData.qr2_code);
  }

  // Consumer Notice
  const consumerNotice = getField('consumer_notice', 16, 460, 7.5);
  const actualFontSize = consumerNotice.font_size || 7.5;
  const lineHeight = Math.ceil(actualFontSize * arialLineSpacingMultiplier);
  tspl += generateTextCmd(consumerNotice.x, consumerNotice.y, consumerNotice.font_size, 'Note: Consumer is free to open and');
  tspl += generateTextCmd(consumerNotice.x, consumerNotice.y + lineHeight, consumerNotice.font_size, 'inspect the product before buying it');

  tspl += `PRINT 1,1\r\n`;

  return tspl;
};
